import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {DateFilterItem, SymbolicDate} from '../common/enums';
import AbstractMap, {connectAbstractMap} from './AbstractMap';
import {loadFeatures} from './FeatureEditorActions';

/**
 * Transakční režim.
 */
const TransactionMode = {
    SINGLE: 'SINGLE',
    PARTIAL: 'PARTIAL',
    MULTI: 'MULTI',
};

/**
 * Typ nástroje.
 */
const ToolType = {
    POINT: 'POINT',
    LINE: 'LINE',
    POLYGON: 'POLYGON',
    FIXED_RECTANGLE: 'FIXED_RECTANGLE',
    SELECT: 'SELECT',
    BOX_SELECT: 'BOX_SELECT',
    SELECT_ALL: 'SELECT_ALL',
    MOVE: 'MOVE',
    EDIT: 'EDIT',
    EDIT_MOVE: 'EDIT_MOVE',
    ROTATE: 'ROTATE',
    ROTATE_MOVE: 'ROTATE_MOVE',
    CUT: 'CUT',
    COPY: 'COPY',
    PASTE: 'PASTE',
    DELETE: 'DELETE',
    MERGE: 'MERGE',
    SPLIT: 'SPLIT',
    INSPECT: 'INSPECT',
    MENU: 'MENU',
};

/**
 * Základní nástroj.
 */
const Tool = {
    POINT: 'POINT',
    LINE: 'LINE',
    POLYGON: 'POLYGON',
    FIXED_RECTANGLE: 'FIXED_RECTANGLE',
    SELECT: 'SELECT',
    BOX_SELECT: 'BOX_SELECT',
    SELECT_ALL: 'SELECT_ALL',
    EDIT: 'EDIT',
    EDIT_MOVE: 'EDIT_MOVE',
    MOVE: 'MOVE',
    ROTATE: 'ROTATE',
    ROTATE_MOVE: 'ROTATE_MOVE',
    CUT: 'CUT',
    COPY: 'COPY',
    PASTE: 'PASTE',
    DELETE: 'DELETE',
    MERGE: 'MERGE',
    SPLIT: 'SPLIT',
    INSPECT: 'INSPECT',
    SEPARATOR: 'SEPARATOR',
};

/**
 * Klávesová zkratka.
 */
const KeyboardShortcut = {
    SELECT_ALL: 'SELECT_ALL',
    CUT: 'CUT',
    COPY: 'COPY',
    PASTE: 'PASTE',
    DELETE: 'DELETE',
};

KeyboardShortcut.ALL = Object.values(KeyboardShortcut);

/**
 * Typ události.
 */
const EventType = {
    SELECT: 'SELECT',
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    ACTION: 'ACTION',
    EXPORT: 'EXPORT',
};

/**
 * Typy properties specifické pro FeatureEditor.
 */
export const editorPropTypes = {
    readOnly: PropTypes.bool,
    transactionMode: PropTypes.oneOf(Object.values(TransactionMode)),
    tools: PropTypes.array,
    contextTools: PropTypes.array,
    keyboardShortcuts: PropTypes.array,
    featureActions: PropTypes.array,
    color: PropTypes.arrayOf(PropTypes.number),
    initialFeatures: PropTypes.array,
    initialFeaturesZoom: PropTypes.bool,
};

/**
 * Výchozí hodnoty properties specifické pro FeatureEditor.
 */
export const editorDefaultProps = {
    readOnly: false,
    transactionMode: null,
    tools: [],
    contextTools: [],
    keyboardShortcuts: [KeyboardShortcut.DELETE],
    featureActions: [],
    color: null,
    initialFeatures: [],
    initialFeaturesZoom: true,
};

/**
 * Názvy synchronizovaných properties specifických pro FeatureEditor.
 */
const editorSyncedProps = [
    'readOnly',
    'transactionMode',
    'tools',
    'contextTools',
    'keyboardShortcuts',
    'featureActions',
    'color'
];

/**
 * Editor mapových zákresů.
 */
class FeatureEditor extends AbstractMap {

    static TransactionMode = TransactionMode;
    static ToolType = ToolType;
    static Tool = Tool;
    static KeyboardShortcut = KeyboardShortcut;
    static EventType = EventType;
    static SymbolicDate = SymbolicDate;
    static DateFilterItem = DateFilterItem;

    static propTypes = {
        ...AbstractMap.propTypes,
        ...editorPropTypes
    };

    static defaultProps = {
        ...AbstractMap.defaultProps,
        ...editorDefaultProps
    };

    static initialSyncedProps = [
        ...AbstractMap.initialSyncedProps,
        ...editorSyncedProps,
    ];

    static syncedProps = [
        ...AbstractMap.syncedProps,
        ...editorSyncedProps,
    ];

    getInitialSyncedProps() {
        return FeatureEditor.initialSyncedProps;
    }

    getSyncedProps() {
        return FeatureEditor.syncedProps;
    }

    componentPostedInitialSyncedProps() {
        const {dispatch, area, initialFeatures, initialFeaturesZoom} = this.props;
        if (initialFeatures.length) {
            dispatch(loadFeatures(area, initialFeatures, {zoom: initialFeaturesZoom}));
        }
    }

    render() {
        const {
            className: customClassName,
            readOnly,
            transactionMode,
            tools,
            contextTools,
            keyboardShortcuts,
            featureActions,
            color,
            initialFeatures,
            initialFeaturesZoom,
            ...otherProps
        } = this.props;

        const src = this.getFrameSrc('embed-editor');
        const className = classNames('mapio-feature-editor', {'read-only': readOnly}, customClassName);

        return this.renderFrame({src, className, ...otherProps})
    }
}

export default connectAbstractMap(FeatureEditor);
